import React, { useState, useRef, useCallback } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Button,
  Divider,
  Alert
} from '@mui/material';
import PhotoCapture from './PhotoCapture';
import IOSTroubleshootingDialog from './IOSTroubleshootingDialog';
import { isMobileDevice, isIOS, getUserAgentInfo } from '../utils';

const PhotoCaptureDemo = () => {
  const [capturedImage, setCapturedImage] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [showTroubleshooting, setShowTroubleshooting] = useState(false);
  const [cameraPermission, setCameraPermission] = useState('checking...');
  const [mediaDevicesSupported, setMediaDevicesSupported] = useState(false);  const photoCaptureRef = useRef(null);

  // Direct camera access check function
  const checkDirectCameraAccess = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'environment' } 
      });
      
      // Camera access successful
      setCameraPermission('granted (verified by access)');
      
      // Clean up stream immediately
      stream.getTracks().forEach(track => track.stop());
    } catch (err) {
      console.log('Direct camera access failed:', err);
      
      // Parse the error to provide better status
      if (err.name === 'NotAllowedError') {
        setCameraPermission('denied');
      } else if (err.name === 'NotFoundError') {
        setCameraPermission('no camera found');
      } else if (err.name === 'NotSupportedError') {
        setCameraPermission('not supported');
      } else {
        setCameraPermission(`error: ${err.name || 'unknown'}`);
      }
    }
  }, []);

  // Check camera permissions and media device support
  const checkCameraPermissions = useCallback(async () => {
    try {
      // Check if MediaDevices API is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setMediaDevicesSupported(false);
        setCameraPermission('MediaDevices API not supported');
        return;
      }
      
      setMediaDevicesSupported(true);

      // Check camera permission status
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const permission = await navigator.permissions.query({ name: 'camera' });
          setCameraPermission(permission.state);
          
          // Listen for permission changes
          permission.onchange = () => {
            setCameraPermission(permission.state);
          };
        } catch (err) {
          // Fallback: try to access camera directly
          console.log('Permission API failed, trying direct access:', err);
          checkDirectCameraAccess();
        }
      } else {
        // Fallback for browsers without permission API
        checkDirectCameraAccess();
      }
    } catch (error) {
      console.error('Error checking camera permissions:', error);
      setCameraPermission('Error checking permissions');
    }
  }, [checkDirectCameraAccess]);

  // Request camera permission explicitly
  const requestCameraPermission = useCallback(async () => {
    try {
      console.log('Explicitly requesting camera permission...');
      
      // Try to access camera with multiple fallback strategies
      const constraints = [
        { video: { facingMode: 'environment' } },
        { video: { facingMode: 'user' } },
        { video: true }
      ];
      
      let stream = null;
      let permissionGranted = false;
      
      for (const constraint of constraints) {
        try {
          console.log('Trying constraint:', constraint);
          stream = await navigator.mediaDevices.getUserMedia(constraint);
          
          if (stream) {
            console.log('Camera access granted with constraint:', constraint);
            setCameraPermission('granted (explicit request)');
            permissionGranted = true;
            
            // Clean up stream immediately
            stream.getTracks().forEach(track => track.stop());
            break;
          }
        } catch (constraintErr) {
          console.log('Constraint failed:', constraint, constraintErr);
          continue;
        }
      }
      
      if (!permissionGranted) {
        setCameraPermission('request failed - try settings');
      }
      
      // Re-check permissions after request
      setTimeout(() => {
        checkCameraPermissions();
      }, 1000);
      
    } catch (err) {
      console.error('Camera permission request failed:', err);
      
      if (err.name === 'NotAllowedError') {
        setCameraPermission('denied by user');
      } else if (err.name === 'NotFoundError') {
        setCameraPermission('no camera device');
      } else {
        setCameraPermission(`request failed: ${err.name || 'unknown error'}`);
      }
    }
  }, [checkCameraPermissions]);

  const handleImageCapture = (image) => {
    setCapturedImage(image);
    console.log('Image captured:', image ? 'Yes' : 'No');
  };

  const handleValidate = () => {
    if (photoCaptureRef.current) {
      const error = photoCaptureRef.current.validate();
      if (error) {
        setValidationErrors({ photo: error });
      } else {
        setValidationErrors({});
        alert('Photo validation passed!');
      }
    }
  };

  const deviceInfo = {
    isMobile: isMobileDevice(),
    isIOS: isIOS(),
    userAgent: navigator.userAgent
  };
  // Check camera permissions on component mount
  React.useEffect(() => {
    checkCameraPermissions();
  }, [checkCameraPermissions]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom align="center">
        PhotoCapture Component Demo
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Device Information
        </Typography>        <Typography variant="body2" color="text.secondary">
          Mobile Device: {deviceInfo.isMobile ? 'Yes' : 'No'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          iOS Device: {deviceInfo.isIOS ? 'Yes' : 'No'}
        </Typography>        {deviceInfo.isIOS && (
          <Typography variant="body2" color="text.secondary">
            iOS Version: {navigator.userAgent.match(/OS (\d+_\d+)/)?.[1]?.replace('_', '.') || 'Unknown'}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          WebView: {getUserAgentInfo().isWebView ? 'Yes' : 'No'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          iOS WebView: {getUserAgentInfo().isIOSWebView ? 'Yes' : 'No'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          MediaDevices API: {mediaDevicesSupported ? '✅ Supported' : '❌ Not Supported'}
        </Typography>
        <Typography variant="body2" color={
          cameraPermission === 'granted' || cameraPermission.includes('granted') ? 'success.main' : 
          cameraPermission === 'denied' ? 'error.main' : 
          cameraPermission === 'prompt' ? 'warning.main' : 'text.secondary'
        }>
          Camera Permission: {cameraPermission === 'granted' ? '✅ Granted' : 
                             cameraPermission === 'denied' ? '❌ Denied' :
                             cameraPermission === 'prompt' ? '⚠️ Prompt' :
                             cameraPermission.includes('granted') ? `✅ ${cameraPermission}` :
                             `❓ ${cameraPermission}`}
        </Typography>        <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
          User Agent: {deviceInfo.userAgent}
        </Typography>
          <Box sx={{ mt: 2 }}>
          <Button 
            variant="outlined" 
            size="small" 
            onClick={checkCameraPermissions}
            sx={{ textTransform: 'none', mr: 1 }}
          >
            🔄 Refresh Camera Status
          </Button>
          {cameraPermission === 'prompt' && (
            <Button 
              variant="contained" 
              size="small" 
              onClick={requestCameraPermission}
              sx={{ textTransform: 'none' }}
              color="primary"
            >
              📷 Request Camera Permission
            </Button>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Photo Capture Test
        </Typography>        {deviceInfo.isMobile ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Mobile Mode:</strong> Using native camera input for better iOS compatibility. 
            {deviceInfo.isIOS ? (
              <>
                <br />
                <strong>iOS Safari Note:</strong> If camera options don't appear, this is a known iOS Safari limitation. 
                <br />
                <strong>Workarounds:</strong>
                <br />
                • Try both camera buttons (different iOS versions respond differently)
                <br />
                • Take a photo with your Camera app, then use "Choose from Photo Library"
                <br />                • Try refreshing the page and allowing camera permissions
                <br />
                • Consider using Chrome or Firefox on iOS for better camera support
                <br />
                <Button 
                  variant="text" 
                  size="small" 
                  onClick={() => setShowTroubleshooting(true)}
                  sx={{ mt: 1, textTransform: 'none' }}
                >
                  📖 View Detailed Troubleshooting Guide
                </Button>
              </>
            ) : (
              <>
                <br />
                "Take Photo" will open your device's camera, "Choose from Gallery" will open photo library.
              </>
            )}
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Desktop Mode:</strong> Using webcam integration for inline camera preview.
          </Alert>
        )}

        <PhotoCapture
          ref={photoCaptureRef}
          onImageCapture={handleImageCapture}
          label="Test Photo"
          required={true}
          aspectRatio="3:4"
          maxSize={1572864}  // 1.5MB
          allowedTypes={["image/jpeg", "image/png", "image/heic"]}
          showValidationErrors={true}
          onValidationChange={(isValid) => {
            console.log('Validation changed:', isValid);
          }}
        />
        
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button variant="outlined" onClick={handleValidate}>
            Validate Photo
          </Button>
          <Button 
            variant="outlined" 
            color="error" 
            onClick={() => {
              setCapturedImage(null);
              if (photoCaptureRef.current) {
                // Reset the component by calling onImageCapture with null
                handleImageCapture(null);
              }
            }}
          >
            Clear Photo
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          Captured Image Status
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <Typography variant="body1">
          Image Captured: {capturedImage ? '✅ Yes' : '❌ No'}
        </Typography>
        {capturedImage && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Image size: ~{Math.round(capturedImage.length * 0.75 / 1024)} KB
            </Typography>
            <Box sx={{ mt: 1, maxWidth: 300 }}>
              <img 
                src={capturedImage} 
                alt="Captured preview" 
                style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
              />
            </Box>
          </Box>
        )}
        
        {Object.keys(validationErrors).length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Validation Errors: {Object.values(validationErrors).join(', ')}          </Alert>
        )}
      </Paper>

      <IOSTroubleshootingDialog 
        open={showTroubleshooting} 
        onClose={() => setShowTroubleshooting(false)} 
      />      {/* Debug Section for iOS */}
      {deviceInfo.isIOS && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'grey.50' }}>
          <Typography variant="h6" gutterBottom>
            iOS Debug Information
          </Typography>
          
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>iOS Safari Camera Permission Issue:</strong>
              <br />
              • iOS requires a user gesture (button click) to request camera permission
              <br />
              • If status shows "prompt" but no prompt appears, the permission request failed silently
              <br />
              • Try the "Request Camera Permission" button above first
            </Typography>
          </Alert>
          
          <Typography variant="body2" sx={{ mb: 2 }}>
            Test the file input behavior directly:
          </Typography>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <input
              type="file"
              accept="image/*"
              capture="environment"
              onChange={(e) => {
                const file = e.target.files[0];
                console.log('Direct file input - Environment:', file);
                if (file) {
                  alert(`File selected: ${file.name} (${file.type})`);
                }
              }}
              style={{ margin: '4px 0' }}
            />
            <Typography variant="caption" color="text.secondary">
              ↑ Direct file input with capture="environment"
            </Typography>
            
            <input
              type="file"
              accept="image/*"
              capture="user"
              onChange={(e) => {
                const file = e.target.files[0];
                console.log('Direct file input - User:', file);
                if (file) {
                  alert(`File selected: ${file.name} (${file.type})`);
                }
              }}
              style={{ margin: '4px 0' }}
            />
            <Typography variant="caption" color="text.secondary">
              ↑ Direct file input with capture="user"
            </Typography>
            
            <input
              type="file"
              accept="image/*"
              capture
              onChange={(e) => {
                const file = e.target.files[0];
                console.log('Direct file input - Generic capture:', file);
                if (file) {
                  alert(`File selected: ${file.name} (${file.type})`);
                }
              }}
              style={{ margin: '4px 0' }}
            />
            <Typography variant="caption" color="text.secondary">
              ↑ Direct file input with generic capture attribute
            </Typography>
            
            <input
              type="file"
              accept="image/*"
              onChange={(e) => {
                const file = e.target.files[0];
                console.log('Direct file input - No capture:', file);
                if (file) {
                  alert(`File selected: ${file.name} (${file.type})`);
                }
              }}
              style={{ margin: '4px 0' }}
            />
            <Typography variant="caption" color="text.secondary">
              ↑ Direct file input without capture attribute (photo library only)
            </Typography>
          </Box>
          
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Troubleshooting Steps:</strong>
              <br />
              1. Click "Request Camera Permission" button above
              <br />
              2. If prompted, allow camera access
              <br />
              3. Try each file input above to see which options appear
              <br />
              4. Check if any show "Take Photo" or camera options
              <br />
              5. If still no camera option, use "Choose from Photo Library" workflow
              <br />
              6. Check Safari Settings → Privacy & Security → Camera
            </Typography>
          </Alert>
        </Paper>
      )}
    </Container>
  );
};

export default PhotoCaptureDemo;
